import { useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { LookupGrid } from "./LookupGrid";
import {
  LOOKUP_VALUE_TYPE_OPTIONS,
  type IndexQuestion,
  type LookupFormValues,
  type LookupTable,
  type LookupValueType,
} from "./types";
import "./style.css";

export interface LookupFormProps {
  mode: "create" | "edit";
  initialValues: LookupFormValues;
  slug: string;
  questions: IndexQuestion[];
  onCancel: () => void;
  onSave: (values: LookupFormValues) => void;
}

export const LookupForm = ({
  mode,
  initialValues,
  slug,
  questions,
  onCancel,
  onSave,
}: LookupFormProps): JSX.Element => {
  const [values, setValues] = useState<LookupFormValues>(initialValues);

  const set = <K extends keyof LookupFormValues>(
    k: K,
    v: LookupFormValues[K]
  ) => setValues((prev) => ({ ...prev, [k]: v }));

  const questionOptions = useMemo(
    () => questions.map((q) => ({ value: q.slug, label: q.name })),
    [questions]
  );
  const index1Question = questions.find((q) => q.slug === values.index1);
  const index2Question = questions.find((q) => q.slug === values.index2);

  const valid = values.name.trim() !== "" && values.index1 !== "";

  const handleSave = () => {
    if (!valid) return;
    onSave(values);
  };

  return (
    <div className="lookups-tab-card">
      <div className="lookups-tab-card__header">
        <div className="lookups-tab-card__title-group">
          <Button className="btnSeawhite" onClick={onCancel}>
            Cancel
          </Button>
          <h2 className="lookups-tab-card__title">
            {mode === "create" ? "Add Lookup" : "Edit Lookup"}
          </h2>
        </div>
        <div className="lookups-tab-card__actions">
          <Button
            className="squareGreenButton"
            disabled={!valid}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-4">
          <Form.Label htmlFor="lookup-name">Name</Form.Label>
          <TextField
            label={null}
            id="lookup-name"
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
            required
          />
        </div>
        <div className="col-sm-4">
          <RequiredReactSelect
            label="Type of value to look up"
            value={LOOKUP_VALUE_TYPE_OPTIONS.find(
              (o) => o.value === values.valueType
            )}
            onChange={(opt: any) =>
              set("valueType", (opt?.value ?? "number") as LookupValueType)
            }
            options={LOOKUP_VALUE_TYPE_OPTIONS}
            placeholder="Select…"
            required
          />
        </div>
        {mode === "edit" && (
          <div className="col-sm-4">
            <Form.Label htmlFor="lookup-slug">Slug</Form.Label>
            <TextField
              label={null}
              id="lookup-slug"
              value={slug}
              onChange={() => undefined}
              readOnly
              disabled
            />
            <small className="text-muted d-block">Used in calculations.</small>
          </div>
        )}
      </div>

      <div className="row mt-2">
        <div className="col-sm-6">
          <RequiredReactSelect
            label="First value to associate (Index 1)"
            value={questionOptions.find((o) => o.value === values.index1)}
            onChange={(opt: any) => set("index1", opt?.value ?? "")}
            options={questionOptions}
            placeholder="Select a question…"
            required
          />
        </div>
        <div className="col-sm-6">
          <RequiredReactSelect
            label="Second value to associate (Index 2, optional)"
            value={
              values.index2
                ? questionOptions.find((o) => o.value === values.index2)
                : { value: "", label: "— None —" }
            }
            onChange={(opt: any) => set("index2", opt?.value ?? "")}
            options={[{ value: "", label: "— None —" }, ...questionOptions]}
            placeholder="— None —"
          />
        </div>
      </div>

      <LookupGrid
        index1Question={index1Question}
        index2Question={index2Question}
        valueType={values.valueType}
        value={values.lookups}
        onChange={(next: LookupTable) => set("lookups", next)}
      />
    </div>
  );
};

export default LookupForm;
